import DeleteIcon from "@mui/icons-material/Delete"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import { useTheme, CircularProgress, Typography, Stack } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import IconButton from "@mui/material/IconButton"
import { ReactElement, useEffect, useState } from "react"
import { KeyedMutator } from "swr"

import MfaChip from "./mfaChip"
import {
    TT_DELETE_USER_BUTTON,
    TT_FALSE_MFA,
    TT_DELETE_OWN_ACCOUNT,
    TT_RESET_MFA_BUTTON,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useDeleteQuicksightUser from "../../../services/middleware/hooks/adminPortal/useDeleteQuicksightUser"
import useResetMfa from "../../../services/middleware/hooks/adminPortal/useResetMfa"
import useUpdateQuicksight from "../../../services/middleware/hooks/adminPortal/useUpdateQuicksight"
import {
    TUserOutput,
    TUsersOutput,
} from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import AppButton from "../../buttons/appButton"
import CustomRadioButton from "../../buttons/customRadioButton"
import AppTooltip from "../../helper/appTooltip"
import useConfirmationDialog from "../../popUps/deleteConfirmation/deleteConfirmation"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IUserRowProps {
    user: TUserOutput
    mutate: KeyedMutator<TUsersOutput>
}

/**
 * Displays each user in a single row within a table
 * It shows user details such as name, email, MFA status, and QuickSight access roles.
 */
const SysAdminUsersRows = ({ user, mutate }: IUserRowProps): ReactElement => {
    const theme = useTheme()
    const { updateQuicksight, isLoading: updateQuicksightLoading } =
        useUpdateQuicksight(user.email, user.fullName, mutate)

    const { deleteQuicksightUser, isLoading: deleteUserLoading } =
        useDeleteQuicksightUser(user.email, user.fullName, mutate)

    const { resetUserMfa, isLoading: resetMfaLoading } = useResetMfa(
        user.userName,
        user.fullName,
        mutate
    )
    const { isEnaSysAdmin, email: loggedInUserEmail } = useGetUserData()
    const isFalseMfa = user.mfa === false
    const tableCellSx = { opacity: isFalseMfa ? 0.5 : 1 }
    const [selected, setSelected] = useState("")
    const { handleConfirmation } = useConfirmationDialog()

    // Set the selected role based on the user's QuickSight access
    useEffect(() => {
        if (user.isInsightsAuthor) {
            setSelected("Author")
        } else if (user.isInsightsReader) {
            setSelected("Reader")
        } else {
            setSelected("None")
        }
    }, [user, setSelected])

    // Display a confirmation dialog before deleting a user
    const deleteConfirmation = (): void => {
        handleConfirmation(
            `${user.fullName} will be permanently deleted and removed`,
            deleteQuicksightUser
        )
    }

    const isRowLoading = (): boolean =>
        updateQuicksightLoading || deleteUserLoading || resetMfaLoading

    const isRowDisabled = (): boolean => isRowLoading() || isFalseMfa

    const isDeleteDisabled = (): boolean =>
        isRowLoading() || user.email === loggedInUserEmail

    // Update the user's QuickSight access role
    const handleButtonClick = async (userRole: string): Promise<void> => {
        const previousSelection = selected
        setSelected(userRole)

        await updateQuicksight(userRole.toUpperCase(), () => {
            setSelected(previousSelection)
        })
    }

    const handleMfaReset = (): void => {
        handleConfirmation(
            `This will reset Multi-Factor Authentication (MFA) for ${user.fullName}. ` +
                `Please ensure they are informed of this change. Upon their next login, they will be required ` +
                `to reset their MFA, after which their existing permissions will be reinstated.`,
            resetUserMfa
        )
    }

    return (
        <AppTooltip title={user.mfa === true ? "" : TT_FALSE_MFA}>
            <AppTableContentRow>
                <AppTableCell sx={tableCellSx}>
                    {isRowLoading() ? (
                        <CircularProgress />
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                            }}
                        >
                            {user.initials}
                        </Avatar>
                    )}
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Stack direction="row">
                        <Typography color="text.primary" marginRight={1}>
                            {user.fullName}
                        </Typography>
                        <DisplayIf condition={isEnaSysAdmin}>
                            <AppTooltip title="This user is an admin">
                                <ShieldOutlinedIcon
                                    sx={{ color: "text.primary" }}
                                />
                            </AppTooltip>
                        </DisplayIf>
                    </Stack>
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    {" "}
                    <Typography color="text.primary">{user.email}</Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx} align="center">
                    <MfaChip isFalseMfa={isFalseMfa} />
                </AppTableCell>
                <AppTableCell align="center" sx={tableCellSx}>
                    <AppTooltip title={TT_RESET_MFA_BUTTON}>
                        <AppButton
                            onClick={handleMfaReset}
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            sx={{
                                padding: "4px 14px",
                                fontSize: "0.75rem",
                                minWidth: "auto",
                                height: "34px",
                                "& .MuiButton-endIcon": {
                                    marginLeft: "2px",
                                },
                                "&.Mui-disabled": {
                                    "@media (prefers-color-scheme: light)": {
                                        backgroundColor: "#BBBBBB",
                                        color: "#666666",
                                    },
                                },
                            }}
                            disabled={isRowDisabled()}
                        >
                            Reset
                        </AppButton>
                    </AppTooltip>
                </AppTableCell>
                <AppTableCell align="center">
                    <span
                        style={{
                            display: "flex",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            justifyContent: "center",
                        }}
                    >
                        <CustomRadioButton
                            label="Author"
                            selected={selected}
                            handleClick={() => handleButtonClick("Author")}
                            disabled={isRowDisabled()}
                        />
                        <CustomRadioButton
                            label="Reader"
                            selected={selected}
                            handleClick={() => handleButtonClick("Reader")}
                            disabled={isRowDisabled()}
                        />
                        <CustomRadioButton
                            label="None"
                            selected={selected}
                            handleClick={() => handleButtonClick("None")}
                            disabled={isRowDisabled()}
                        />
                    </span>
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip
                        title={
                            isDeleteDisabled()
                                ? TT_DELETE_OWN_ACCOUNT
                                : TT_DELETE_USER_BUTTON
                        }
                    >
                        <span>
                            <IconButton
                                color="error"
                                onClick={deleteConfirmation}
                                disabled={isDeleteDisabled()}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </AppTooltip>
                </AppTableCell>
            </AppTableContentRow>
        </AppTooltip>
    )
}

export default SysAdminUsersRows
